// Single sign-on helpers: silent auto-login, loop protection, error texts.
const SKIP_KEY = 'rhp-sso-skip';
const LAST_TRY_KEY = 'rhp-sso-last-try';
const RETRY_AFTER_MS = 60_000;

/**
 * Don't try the silent SSO login (again): after logout / when the IdP had no session (per-tab flag),
 * or when a silent attempt started less than a minute ago (hard loop protection).
 */
export function skipAutoLogin(): boolean {
  try {
    if (sessionStorage.getItem(SKIP_KEY) === '1') return true;
    return Date.now() - Number(sessionStorage.getItem(LAST_TRY_KEY) ?? 0) < RETRY_AFTER_MS;
  } catch {
    return true; // storage blocked → never risk a redirect loop
  }
}

export function setSkipAutoLogin() {
  try {
    sessionStorage.setItem(SKIP_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function markAutoLoginAttempt() {
  try {
    sessionStorage.setItem(LAST_TRY_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

/** Full-page navigation (own function so tests can replace it). */
export function navigate(url: string, replace = false) {
  if (replace) window.location.replace(url);
  else window.location.assign(url);
}

export function ssoLoginUrl(returnTo: string, silent = false): string {
  return `/api/auth/oidc/login?${new URLSearchParams({ returnTo, ...(silent ? { silent: '1' } : {}) })}`;
}

export const SSO_ERRORS: Record<string, string> = {
  no_access: 'Dein Konto hat keinen Zugriff auf den Haus Planer. Bitte einen Admin, dich der passenden Gruppe hinzuzufügen.',
  expired: 'Die Anmeldung hat zu lange gedauert. Bitte erneut versuchen.',
  unavailable: 'Der Anmeldedienst ist gerade nicht erreichbar.',
  failed: 'Die Anmeldung ist fehlgeschlagen.',
};

/** Reads and removes `sso` / `sso_error` from the address bar after the IdP redirect. */
export function consumeSsoParams(): { none: boolean; error: string | null } {
  const url = new URL(window.location.href);
  const none = url.searchParams.get('sso') === 'none';
  const error = url.searchParams.get('sso_error');
  if (none || error) {
    url.searchParams.delete('sso');
    url.searchParams.delete('sso_error');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    setSkipAutoLogin();
  }
  return { none, error };
}
